import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Shield } from 'lucide-react';

export const AdminLoginPage = () => {
    const { adminLogin, adminRegister } = useAuth();
    const { language, toggleLanguage } = useLanguage();
    const navigate = useNavigate();
    const [isRegister, setIsRegister] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password || (isRegister && !name)) {
            toast.error(language === 'id' ? 'Mohon isi semua field' : 'Please fill all fields');
            return;
        }

        setLoading(true);
        try {
            if (isRegister) {
                await adminRegister(name, email, password);
                toast.success(language === 'id' ? 'Registrasi admin berhasil!' : 'Admin registration successful!');
            } else {
                await adminLogin(email, password);
                toast.success(language === 'id' ? 'Login admin berhasil!' : 'Admin login successful!');
            }
            navigate('/admin');
        } catch (error) {
            const message = error.response?.data?.detail || (language === 'id' ? 'Operasi gagal' : 'Operation failed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-slate-900">
            {/* Header */}
            <header className="p-4 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-3">
                    <img 
                        src="/assets/logo.jpg" 
                        alt="Sekolah NusaTraders" 
                        className="w-12 h-12 rounded-lg object-contain"
                    />
                    <div>
                        <span className="font-heading font-bold text-lg text-white block leading-tight">SEKOLAH</span>
                        <span className="font-heading font-bold text-sm text-primary block leading-tight">NUSATRADERS</span>
                    </div>
                </Link>
                <div className="flex gap-1 bg-slate-800 rounded-full p-1">
                    <button
                        onClick={() => language !== 'id' && toggleLanguage()}
                        className={`px-3 py-1 text-sm rounded-full ${language === 'id' ? 'bg-primary text-white' : 'text-slate-400'}`}
                    >
                        ID
                    </button>
                    <button
                        onClick={() => language !== 'en' && toggleLanguage()}
                        className={`px-3 py-1 text-sm rounded-full ${language === 'en' ? 'bg-primary text-white' : 'text-slate-400'}`}
                    >
                        EN
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-md bg-slate-800 border-slate-700">
                    <CardHeader className="text-center">
                        <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                            <Shield className="w-7 h-7 text-primary" />
                        </div>
                        <CardTitle className="text-2xl font-heading text-white">
                            {isRegister 
                                ? (language === 'id' ? 'Daftar Admin' : 'Admin Register')
                                : (language === 'id' ? 'Login Admin' : 'Admin Login')
                            }
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            {language === 'id' ? 'Akses panel admin Sekolah NusaTraders' : 'Access Sekolah NusaTraders admin panel'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {isRegister && (
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="text-slate-300">
                                        {language === 'id' ? 'Nama' : 'Name'}
                                    </Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder={language === 'id' ? 'Nama admin' : 'Admin name'}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={loading}
                                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                        data-testid="admin-name"
                                    />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-300">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                    data-testid="admin-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-300">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                                        data-testid="admin-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="admin-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'id' ? 'Memproses...' : 'Processing...'}
                                    </>
                                ) : isRegister ? (
                                    language === 'id' ? 'Daftar' : 'Register'
                                ) : (
                                    language === 'id' ? 'Masuk' : 'Login'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <button
                                onClick={() => setIsRegister(!isRegister)}
                                className="text-primary hover:underline"
                                data-testid="toggle-register"
                            >
                                {isRegister 
                                    ? (language === 'id' ? 'Sudah punya akun? Login' : 'Already have an account? Login')
                                    : (language === 'id' ? 'Belum punya akun? Daftar' : "Don't have an account? Register")
                                }
                            </button>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                            <Link 
                                to="/login" 
                                className="text-sm text-slate-400 hover:text-slate-300"
                                data-testid="user-login-link"
                            >
                                {language === 'id' ? 'Login sebagai User' : 'Login as User'}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};
